import { DiagnosticCardData } from './diagnosticCard';
import { parseParentDiagnostic, splitRecommendationName } from './packCard';
import {
  ALL,
  DIAGNOSTIC_CLASSIFICATION_ID,
  Pack,
  StudentCompletion,
  UnitProgress,
  diagnosticSummaryLink,
  isProperDiagnosticUnit,
  sortByStatusThenLastName,
  studentCompletionsFor,
} from './shared';
import { DiagnosticClassroom } from './usePackData';

const POST_PHASE = 'Post'

export function createPackResolver({ packs }: { packs: Pack[], selectedClassroomId: string | number | null }) {
  function packsBackingActivity(activityId: number | string, classroomId: number | string): Pack[] {
    return packs.filter(pack =>
      pack.activities.some(a => String(a.activityId) === String(activityId)) &&
      pack.classrooms.some(c => String(c.id) === String(classroomId))
    )
  }

  function preferOpenProperPack(matches: Pack[]): Pack | undefined {
    const openMatches = matches.filter(pack => pack.open !== false)
    return openMatches.find(isProperDiagnosticUnit) || matches.find(isProperDiagnosticUnit) || openMatches[0] || matches[0]
  }

  function packsByUnitIds(unitIds?: Array<number | string>): Pack[] {
    if (!unitIds?.length) { return [] }
    const idSet = new Set(unitIds.map(String))
    return packs.filter(pack => idSet.has(String(pack.unitId)))
  }

  function backingPackFor(card: DiagnosticCardData): Pack | undefined {
    const exactMatches = packsByUnitIds(card.pre.unit_ids)
    if (exactMatches.length) { return preferOpenProperPack(exactMatches) }
    return preferOpenProperPack(packsBackingActivity(card.pre.activity_id, card.classroomId))
  }

  function postBackingPackFor(card: DiagnosticCardData): Pack | undefined {
    const post = card.post as { activity_id?: number, unit_ids?: number[] } | undefined
    if (!post?.activity_id) { return undefined }
    const exactMatches = packsByUnitIds(post.unit_ids)
    if (exactMatches.length) { return preferOpenProperPack(exactMatches) }
    return preferOpenProperPack(packsBackingActivity(post.activity_id, card.classroomId))
  }

  function diagnosticCardIsOpen(card: DiagnosticCardData): boolean {
    const backingPack = backingPackFor(card)
    if (backingPack) { return backingPack.open !== false }
    const postBackingPack = postBackingPackFor(card)
    if (postBackingPack) { return postBackingPack.open !== false }
    return true
  }

  return {
    backingPackFor,
    diagnosticCardIsOpen,
    packsBackingActivity,
    packsByUnitIds,
    postBackingPackFor,
    preferOpenProperPack,
  }
}

export type PackResolver = ReturnType<typeof createPackResolver>

function diagnosticHasAssignedStudents(card: DiagnosticCardData): boolean {
  return card.pre.assigned_count > 0
}

export function buildAllDiagnosticCards(diagnosticClassrooms: DiagnosticClassroom[], packResolver: PackResolver): DiagnosticCardData[] {
  const { backingPackFor } = packResolver
  return diagnosticClassrooms
    .flatMap(classroom => (classroom.diagnostics || []).map(diagnostic => ({
      classroomId: classroom.id,
      classroomName: classroom.name,
      name: diagnostic.name,
      pre: diagnostic.pre,
      post: diagnostic.post,
    })))
    .filter(card => card.pre)
    .filter(diagnosticHasAssignedStudents)
    .filter(card => {
      const backingPack = backingPackFor(card)
      return !backingPack || isProperDiagnosticUnit(backingPack)
    })
}

export function createCardResolver({ packResolver, allDiagnosticCards, unitProgress, selectedClassroomId }: {
  packResolver: PackResolver,
  allDiagnosticCards: DiagnosticCardData[],
  unitProgress: Record<string, UnitProgress>,
  selectedClassroomId: string | number | null,
}) {
  const { backingPackFor, diagnosticCardIsOpen, packsBackingActivity, postBackingPackFor } = packResolver

  function diagnosticStudentsFor(activityId: number | string, classroomId: number | string): StudentCompletion[] {
    const rank = (s: StudentCompletion) => (s.completed ? 2 : (s.started ? 1 : 0))
    const byId = new Map<number, StudentCompletion>()
    packsBackingActivity(activityId, classroomId).forEach(pack => {
      studentCompletionsFor(unitProgress[pack.unitId], classroomId).forEach(student => {
        const existing = byId.get(student.id)
        if (!existing || rank(student) > rank(existing)) { byId.set(student.id, student) }
      })
    })
    return sortByStatusThenLastName(Array.from(byId.values()))
  }

  function parentCardFor(pack: Pack): { card: DiagnosticCardData, parentActivityId: number } | undefined {
    const progress = unitProgress[pack.unitId]
    if (!progress) { return undefined }
    const classroomParentId = selectedClassroomId === ALL
      ? null
      : progress.classrooms[String(selectedClassroomId)]?.parent_diagnostic_activity_id
    const candidateIds = (progress.parent_diagnostic_activity_ids || []).filter(id => id !== classroomParentId)
    if (classroomParentId) { candidateIds.unshift(classroomParentId) }
    for (const parentActivityId of candidateIds) {
      const card = allDiagnosticCards.find(c =>
        cardMatchesActivity(c, String(parentActivityId)) &&
        (selectedClassroomId === ALL
          ? pack.classrooms.some(pc => String(pc.id) === String(c.classroomId))
          : String(c.classroomId) === String(selectedClassroomId)))
      if (card) { return { card, parentActivityId } }
    }
    return undefined
  }

  function parentDiagnosticHref(pack: Pack): string | undefined {
    if (!unitProgress[pack.unitId]?.from_recommendations) { return undefined }

    const resolved = parentCardFor(pack)
    if (resolved) {
      const { card: idCard, parentActivityId } = resolved
      const isPost = String(idCard.pre.activity_id) !== String(parentActivityId)
      if (isPost) { return diagnosticSummaryLink(parentActivityId, idCard.classroomId, postBackingPackFor(idCard)?.unitId, true) }
      return diagnosticSummaryLink(idCard.pre.activity_id, idCard.classroomId, backingPackFor(idCard)?.unitId)
    }

    const { parentDiagnostic } = splitRecommendationName(pack.unitName, true)
    if (!parentDiagnostic) { return undefined }
    const parent = parseParentDiagnostic(parentDiagnostic)
    const card = allDiagnosticCards.find(c => String(c.classroomId) === String(selectedClassroomId) && c.name === parent.family)
    if (!card) { return undefined }
    const isPost = parent.phase === POST_PHASE
    const post = card.post as { activity_id?: number } | undefined
    if (isPost) {
      const activityId = card.pre.post_test_id || post?.activity_id
      if (!activityId) { return undefined }
      return diagnosticSummaryLink(activityId, card.classroomId, postBackingPackFor(card)?.unitId, true)
    }
    if (!card.pre.activity_id) { return undefined }
    return diagnosticSummaryLink(card.pre.activity_id, card.classroomId, backingPackFor(card)?.unitId)
  }

  function parentDiagnosticLabel(pack: Pack): string | undefined {
    if (!unitProgress[pack.unitId]?.from_recommendations) { return undefined }
    const resolved = parentCardFor(pack)
    if (!resolved) { return undefined }
    const { card, parentActivityId } = resolved
    const isPost = String(card.pre.activity_id) !== String(parentActivityId)
    return `${card.name} · ${isPost ? 'Growth Post' : 'Baseline Pre'}`
  }

  function cardMatchesActivity(card: DiagnosticCardData, activityId: string) {
    if (String(card.pre.activity_id) === activityId) { return true }
    const post = card.post as { activity_id?: number } | undefined
    return Boolean(post?.activity_id && String(post.activity_id) === activityId)
  }

  function diagnosticCardForPack(pack: Pack): DiagnosticCardData | undefined {
    const diagnosticActivity = pack.activities.find(a => Number(a.classificationId) === DIAGNOSTIC_CLASSIFICATION_ID)
    if (!diagnosticActivity) { return undefined }
    return allDiagnosticCards.find(card =>
      cardMatchesActivity(card, String(diagnosticActivity.activityId)) &&
      (selectedClassroomId === ALL
        ? pack.classrooms.some(c => String(c.id) === String(card.classroomId))
        : String(card.classroomId) === String(selectedClassroomId) && pack.classrooms.some(c => String(c.id) === String(card.classroomId)))
    )
  }

  function assignedCountInSelectedClass(pack: Pack): number {
    const relevantClassrooms = selectedClassroomId === ALL
      ? pack.classrooms
      : pack.classrooms.filter(c => String(c.id) === String(selectedClassroomId))
    return relevantClassrooms.reduce((sum, c) => sum + Number(c.assignedStudentCount || 0), 0)
  }

  function diagnosticRendersAsCustomPack(pack: Pack): boolean {
    if (!isProperDiagnosticUnit(pack)) { return false }
    if (assignedCountInSelectedClass(pack) === 0) { return false }
    return !diagnosticCardForPack(pack)
  }

  function packIsCardAnchor(pack: Pack, card: DiagnosticCardData): boolean {
    return [backingPackFor(card), postBackingPackFor(card)].some(anchor => anchor && String(anchor.unitId) === String(pack.unitId))
  }

  function packRendersJourneyCard(pack: Pack, card: DiagnosticCardData): boolean {
    return packIsCardAnchor(pack, card) && (pack.open !== false) === diagnosticCardIsOpen(card)
  }

  function diagnosticBlobKey(pack: Pack): string | undefined {
    if (!isProperDiagnosticUnit(pack)) { return undefined }
    const card = diagnosticCardForPack(pack)
    return card && packRendersJourneyCard(pack, card) ? `${card.classroomId}-${card.pre.activity_id}` : undefined
  }

  return {
    assignedCountInSelectedClass,
    cardMatchesActivity,
    diagnosticBlobKey,
    diagnosticCardForPack,
    diagnosticRendersAsCustomPack,
    diagnosticStudentsFor,
    packIsCardAnchor,
    packRendersJourneyCard,
    parentDiagnosticHref,
    parentDiagnosticLabel,
  }
}
